import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Briefcase, Search, Filter, MapPin, Clock, Mail, LogOut, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function DirectoryPage() {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [filteredBusinesses, setFilteredBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('all');
  const [selectedTag, setSelectedTag] = useState('all');
  const [allTags, setAllTags] = useState([]);
  const [user, setUser] = useState(null);

  const industries = ['Technology', 'Construction', 'Retail', 'Healthcare', 'Education', 'Hospitality', 'Manufacturing', 'Other'];

  useEffect(() => {
    fetchUser();
    fetchBusinesses();
    fetchTags();
  }, []);

  useEffect(() => {
    filterBusinesses();
  }, [searchQuery, selectedIndustry, selectedTag, businesses]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      // User not logged in, that's okay
    }
  };

  const fetchTags = async () => {
    try {
      const response = await axios.get(`${API}/tags`);
      setAllTags(response.data);
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  const fetchBusinesses = async () => {
    try {
      const response = await axios.get(`${API}/businesses`);
      setBusinesses(response.data);
      setFilteredBusinesses(response.data);
    } catch (error) {
      console.error('Error fetching businesses:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterBusinesses = () => {
    let filtered = [...businesses];

    if (searchQuery) {
      filtered = filtered.filter(
        (b) =>
          b.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          b.philosophy.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (selectedIndustry !== 'all') {
      filtered = filtered.filter((b) => b.industry === selectedIndustry);
    }

    if (selectedTag !== 'all') {
      filtered = filtered.filter((b) => b.tags && b.tags.includes(selectedTag));
    }

    setFilteredBusinesses(filtered);
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="directory-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="container mx-auto px-6 md:px-12 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <img src="/s2b-hub-logo.png" alt="The Hub by S2B Group" className="h-12 w-12 object-contain" />
            <div className="flex flex-col leading-none"><span className="text-2xl font-outfit font-bold text-primary">The Hub</span><span className="text-xs text-slate-600 font-normal">by S2B Group</span></div>
          </div>
          <div className="flex items-center gap-4">
            {user ? (
              <>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/dashboard')}
                  className="text-primary hover:bg-primary/5"
                  data-testid="dashboard-link"
                >
                  Dashboard
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleLogout}
                  className="text-primary hover:bg-primary/5"
                  data-testid="logout-button"
                >
                  <LogOut className="h-5 w-5" />
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/login')}
                  className="text-primary hover:bg-primary/5"
                  data-testid="login-link"
                >
                  Log in
                </Button>
                <Button
                  onClick={() => navigate('/signup')}
                  className="bg-accent-orange text-white hover:bg-[#D97000] rounded-full px-8 py-3"
                  data-testid="signup-link"
                >
                  Sign up
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero Search */}
      <section className="bg-primary py-16">
        <div className="container mx-auto px-6 md:px-12">
          <h1 className="text-4xl md:text-5xl font-outfit font-bold text-white text-center mb-4">
            Business Directory
          </h1>
          <p className="text-lg text-white/80 text-center mb-8">
            Discover and connect with South African businesses
          </p>
          <div className="max-w-3xl mx-auto flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Search businesses..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white border-white/20 h-12 rounded-lg"
                data-testid="search-input"
              />
            </div>
            <Select value={selectedIndustry} onValueChange={setSelectedIndustry}>
              <SelectTrigger className="w-full sm:w-48 bg-white h-12 rounded-lg" data-testid="industry-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Industry" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Industries</SelectItem>
                {industries.map((ind) => (
                  <SelectItem key={ind} value={ind}>
                    {ind}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {allTags.length > 0 && (
              <Select value={selectedTag} onValueChange={setSelectedTag}>
                <SelectTrigger className="w-full sm:w-48 bg-white h-12 rounded-lg" data-testid="tag-filter">
                  <Tag className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Tags" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tags</SelectItem>
                  {allTags.map((tag) => (
                    <SelectItem key={tag} value={tag}>
                      {tag}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="container mx-auto px-6 md:px-12 py-12">
        {loading ? (
          <div className="text-center py-12" data-testid="loading-businesses">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-orange mx-auto"></div>
            <p className="mt-4 text-slate-600">Loading businesses...</p>
          </div>
        ) : filteredBusinesses.length > 0 ? (
          <>
            <p className="text-slate-600 mb-8" data-testid="results-count">
              Showing {filteredBusinesses.length} {filteredBusinesses.length === 1 ? 'business' : 'businesses'}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredBusinesses.map((business) => (
                <BusinessCard key={business.business_id} business={business} user={user} />
              ))}
            </div>
          </>
        ) : (
          <div className="text-center py-12" data-testid="no-results">
            <Briefcase className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <p className="text-xl text-slate-600">No businesses found</p>
            <p className="text-slate-500 mt-2">Try adjusting your search criteria</p>
          </div>
        )}
      </section>
    </div>
  );
}

function BusinessCard({ business, user }) {
  const navigate = useNavigate();

  const handleContact = () => {
    if (!user) {
      navigate('/login');
      return;
    }
    navigate('/chat', { state: { businessId: business.business_id, targetUserId: business.user_id } });
  };

  return (
    <div
      className="bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md hover:-translate-y-1 group overflow-hidden cursor-pointer"
      onClick={() => navigate(`/business/${business.business_id}`)}
      data-testid="business-card"
    >
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="text-xl font-outfit font-semibold text-primary mb-1 group-hover:text-accent-orange">
              {business.name}
            </h3>
            <p className="text-sm text-slate-600">{business.industry}</p>
          </div>
          {business.is_verified && (
            <span className="px-3 py-1 bg-accent-cyan/10 text-accent-cyan text-xs font-medium rounded-full">
              Verified
            </span>
          )}
        </div>
        <p className="text-slate-600 text-sm mb-4 line-clamp-2">{business.philosophy}</p>
        
        {/* Tags */}
        {business.tags && business.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-4" data-testid="business-tags">
            {business.tags.slice(0, 3).map((tag) => (
              <Badge 
                key={tag} 
                variant="secondary"
                className="bg-slate-100 text-slate-700 text-xs px-2 py-0.5"
              >
                {tag}
              </Badge>
            ))}
            {business.tags.length > 3 && (
              <Badge variant="secondary" className="bg-slate-100 text-slate-700 text-xs px-2 py-0.5">
                +{business.tags.length - 3}
              </Badge>
            )}
          </div>
        )}
        
        <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
          <div className="flex items-center gap-1">
            <Clock className="h-4 w-4" />
            <span>{business.years_experience} years</span>
          </div>
          {business.owner && (
            <div className="flex items-center gap-1">
              <Mail className="h-4 w-4" />
              <span className="truncate">{business.owner.name}</span>
            </div>
          )}
        </div>
        <Button
          onClick={(e) => {
            e.stopPropagation();
            handleContact();
          }}
          variant="outline"
          className="w-full border-slate-200 hover:bg-accent-orange hover:text-white rounded-lg"
          data-testid="contact-button"
        >
          Contact
        </Button>
      </div>
    </div>
  );
}

export default DirectoryPage;
